import React, { useEffect, useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import LibationMergeModal from '../../../components/listingDetail/Wines/LibationMergeModal';
import client from '../../../lib/feathers';

// Review queue for likely-duplicate libations within a listing, surfaced by the
// nightly find-libation-near-dupes job. Merge (via the existing LibationMergeModal,
// which re-points sips/LVs/library) or mark "not duplicates" (dismiss — the finder
// won't re-surface that set).
export default function DuplicateLibations() {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [mergeGroup, setMergeGroup] = useState(null); // group whose merge modal is open

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await client.service('admin/libation-duplicate-group').find({
        query: { status: 'pending', $limit: 50, $sort: { minScore: -1 } },
      });
      setGroups(res.data || []);
    } catch (e) {
      setError('Failed to load duplicate groups.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (group, patch) => {
    setBusyId(group.id);
    try {
      await client.service('admin/libation-duplicate-group').patch(group.id, patch);
      setGroups((cur) => (cur || []).filter((g) => g.id !== group.id));
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = (g) => resolve(g, { status: 'dismissed' });

  // "Not this one" — pull a single wine out of the group. If only a pair is left
  // it's just "not duplicates" (dismiss the whole pair); otherwise split it off
  // (records it as distinct from the rest, keeps the rest pending to merge).
  const notThisOne = async (group, libId) => {
    const remaining = (group.libations || []).filter((l) => l.id !== libId);
    setBusyId(group.id);
    try {
      if (remaining.length < 2) {
        await client.service('admin/libation-duplicate-group').patch(group.id, { status: 'dismissed' });
        setGroups((cur) => (cur || []).filter((g) => g.id !== group.id));
      } else {
        await client.service('admin/libation-duplicate-group').patch(group.id, { splitIds: [libId] });
        setGroups((cur) => (cur || []).map((g) => (g.id === group.id
          ? { ...g, libations: remaining, libationIds: remaining.map((l) => l.id) }
          : g)));
      }
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // After a merge completes in the modal, mark the group resolved and drop it.
  const onMergeComplete = () => {
    const g = mergeGroup;
    setMergeGroup(null);
    if (g) resolve(g, { status: 'resolved' });
  };

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Reports', href: '/admin/reports' },
          { name: 'Duplicate Libations' },
        ]}
        />
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-1">Duplicate Libations</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Likely-duplicate wines within a single listing (the same wine entered or
          imported more than once), found by name-similarity clustering. Review each
          set and <strong>Merge</strong> the duplicates into one (re-points sips,
          vintages, and cellar entries), or mark <strong>not duplicates</strong> —
          dismissed sets won&apos;t be surfaced again.
        </p>
      </div>

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && groups === null && <div className="text-sm text-gray-500">Loading…</div>}
      {!error && groups && groups.length === 0 && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500 italic">
          No duplicate libations to review. The nightly finder will add new ones here.
        </div>
      )}

      {!error && groups && groups.length > 0 && (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.id} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center gap-4">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">
                    {g.listingId ? (
                      <NavLink to={`/admin/listings/${g.listingId}`} className="text-indigo-700 hover:underline">
                        {g.listingName || 'listing'}
                      </NavLink>
                    ) : (g.listingName || 'listing')}
                  </h3>
                  <div className="mt-1 text-xs text-gray-500">
                    {(g.libations || []).length} similar wines
                    {typeof g.minScore === 'number' && ` · match ${(g.minScore * 100).toFixed(0)}%`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => dismiss(g)}
                    disabled={busyId === g.id}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    title="These are different wines — never group them again."
                  >
                    Not duplicates
                  </button>
                  <button
                    type="button"
                    onClick={() => setMergeGroup(g)}
                    disabled={busyId === g.id || (g.libations || []).length < 2}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50"
                  >
                    Merge…
                  </button>
                </div>
              </div>

              <div className="px-4 py-3">
                <ul className="divide-y divide-gray-100">
                  {(g.libations || []).map((l) => {
                    const isKeeper = g.representativeId === l.id;
                    return (
                      <li key={l.id} className="py-2 flex items-center gap-3 text-sm">
                        <span className="font-medium text-gray-900 truncate">{l.name}</span>
                        {isKeeper && (
                          <span className="bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded shrink-0">
                            suggested keeper
                          </span>
                        )}
                        {l.brand && <span className="text-xs text-gray-500 shrink-0">brand: {l.brand}</span>}
                        <span className="ml-auto flex items-center gap-2 text-xs text-gray-400 shrink-0">
                          <span>{l.vintageCount} {l.vintageCount === 1 ? 'vintage' : 'vintages'}</span>
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{l.source}</span>
                          {l.status !== 'active' && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{l.status}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => notThisOne(g, l.id)}
                            disabled={busyId === g.id}
                            className="ml-1 text-gray-400 hover:text-red-700 disabled:opacity-50"
                            title="Not a duplicate — remove this wine from the group (won't be grouped with the others again)"
                          >
                            ✕
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-2 text-xs text-gray-400">
                  Merge opens the standard wine-merge tool — pick the keeper and resolve any
                  vintage conflicts there. The suggested keeper is the one with the most vintages.
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <LibationMergeModal
        open={!!mergeGroup}
        onClose={() => setMergeGroup(null)}
        onComplete={onMergeComplete}
        libations={(mergeGroup && mergeGroup.libations) || []}
      />
    </Page>
  );
}
